import React, {
  Dispatch,
  SetStateAction,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { NavigateOptions } from 'react-router';
import { Dialog, Toast } from 'antd-mobile';
import { useDomDialog, useNav } from '@/hooks';
import { ALL_PAGES } from '@/constant';
import service from '@/service';
import toastify from '@/utils/toastify';
import { selectCurrentServingOrder } from '@/store/service';
import { SocketClientType } from '@/service/socketio';
import { MassageControlValueContext } from '@/pages/service/massage-control';
import { isNewSoftwareVersion } from '@/utils';
import { DialogAction, RobotExceptionConfig, RobotExceptionEventsHandler, RobotExceptionValues } from './utils';

const detectingAreaCode: MassageRuntime.RobotExceptionEvents[] = [
  'DETECTING_POSE_ERROR',
  'DETECTING_NOT_FLAT_LYING',
  'DETECTING_NOT_STAYING',
  'DETECTING_GOT_DISRUPTOR',
  'HAND_BLOCKING_MASSAGE_AREA',
  'ABDOMEN_DISRUPTORS',
  'DEPTH_MAP_ABNORMAL',
];

interface Props {
  isIgnoreTaskFinished: React.MutableRefObject<boolean>;
  socketInstance: React.MutableRefObject<SocketClientType>;
  currentProcess?: Service.SubStep;
}

type ShowOrderErrorPromptType = {
  navUrl?: ALL_PAGES;
  navOptions?: NavigateOptions;
  orderId?: string;
  shockwaveProbeCount?: number;
  finishedReason?:
    | 'User' // 用户主动结束，包括UI和语音
    | 'Normal' // 正常执行完结束
    | 'Error' // 异常结束
    | 'ErrorWireStraightened'; // 扯线异常
};

interface RefAttrs {
  hanldeRobotExceptionEvents: (code: MassageRuntime.RobotExceptionEvents) => void;
  hanldeRobotExceptionRecoverEvents: (code: MassageRuntime.RobotExceptionEvents) => void;
  showOrderErrorPrompt: (config: ShowOrderErrorPromptType) => void;
  setIsConfirmBtnDisabled: Dispatch<SetStateAction<Partial<Record<MassageRuntime.RobotExceptionEvents, boolean>>>>;
}

export type { RefAttrs as RobotExceptionPromptType };

type IsConfirmBtnDisabledType = Partial<Record<MassageRuntime.RobotExceptionEvents, boolean>>;

const RobotExceptionPrompt = forwardRef<RefAttrs, Props>(
  ({ isIgnoreTaskFinished, socketInstance, currentProcess }, ref) => {
    const nav = useNav();
    const { massageState } = useContext(MassageControlValueContext);
    const currentServingOrder = useSelector(selectCurrentServingOrder);
    const currentIsShowL0Error = useRef(false);
    const currentIsShowL1Error = useRef(false);
    const curDialogActions = useRef<{
      confirm: (params: DialogAction) => void | Promise<void>;
      cancel: (params: DialogAction) => void;
    }>({
      confirm: null,
      cancel: null,
    });
    const [diaglogParams, setDiaglogParams] = useState<RobotExceptionValues>(null);
    const [isConfirmBtnDisabled, setIsConfirmBtnDisabled] = useState<IsConfirmBtnDisabledType>({});
    const currentErrEventsRef = useRef<MassageRuntime.RobotExceptionEvents>(null);
    const clearToastLoading = useRef<{ [key: string]: () => void }>({});

    // 当前是否是自定义画线方案
    const currentIsCustomLinePlan = useMemo(
      () => currentServingOrder?.modelConfig?.[0]?.value === 'custom-draw-line',
      [currentServingOrder]
    );

    const { dialogProps, show, hide } = useDomDialog({
      ...diaglogParams,
      isConfirmTextDisabled:
        diaglogParams?.confirmText?.includes('继续') &&
        /**
         * 不允许暂停逻辑：
         * 1. 按摩状态不为暂停 或
         * 2. 当前是自定义画线方案
         */
        ((massageState !== undefined && massageState !== 'Paused') || currentIsCustomLinePlan)
          ? true
          : !!isConfirmBtnDisabled?.[currentErrEventsRef.current],
      async onClickConfirm() {
        isIgnoreTaskFinished.current = false;
        if (curDialogActions.current.confirm) {
          await curDialogActions.current.confirm?.({
            nav,
            async extraActions(params) {
              if (
                ['WIRE_STRAIGHTENED', 'FORCE_CONTROL_PROTECT', 'HOTSTONE_DISCONNECT', 'NO_HUMAN_IN_MASSAGE']?.includes(
                  params?.type
                )
              ) {
                try {
                  await socketInstance?.current?.resume();
                  hide();
                } catch (e) {
                  console.error(e);
                }
              } else if (
                [
                  'ARM_ERROR',
                  'TRACK_CALC_FAILED',
                  'ACUPOINTS_RECOG_FAILED',
                  'RF_LOSE_CONTROL',
                  'EMERGENCY_STOP',
                  'UNRECOVERABLE_ARM_ERROR',
                  'SHOCKWAVE_NOT_AVAILABLE',
                ].includes(params.type)
              ) {
                try {
                  // 结束订单
                  await service.service.interrupt({
                    orderId: currentServingOrder?.orderId,
                    errorMessage: params.type,
                  });
                  nav(ALL_PAGES.MEMBER_DETAIL, {
                    replace: true,
                    state: {
                      orderId: currentServingOrder?.orderId,
                      isNeedShowDrawerAfterFinish: false,
                    },
                  });
                } catch (e) {
                  console.error(e);
                }
              }
            },
          });
          hide();
          curDialogActions.current.confirm = null;
        } else {
          hide();
        }
        currentIsShowL1Error.current = false;
      },
      onClickCancel() {
        isIgnoreTaskFinished.current = false;
        currentIsShowL1Error.current = false;
        return new Promise(resolve => {
          if (curDialogActions.current.cancel) {
            curDialogActions.current.cancel({
              nav,
              async extraActions(params) {
                if (
                  [
                    'ARM_WORKSPACE_ERROR',
                    'FORCE_CONTROL_PROTECT',
                    'WIRE_STRAIGHTENED',
                    'HOTSTONE_DISCONNECT',
                    'NO_HUMAN_IN_MASSAGE',
                  ].includes(params.type)
                ) {
                  try {
                    await service.service.interrupt({
                      orderId: currentServingOrder?.orderId,
                      errorMessage: params.type,
                    });
                    nav(ALL_PAGES.MEMBER_DETAIL, {
                      replace: true,
                      state: {
                        orderId: currentServingOrder?.orderId,
                        isNeedShowDrawerAfterFinish: false,
                      },
                    });
                  } catch (e) {
                    console.error(e);
                  }
                }
              },
              onFinally() {
                curDialogActions.current.cancel = null;
                resolve('');
                hide();
              },
            });
          } else {
            hide();
            resolve('');
          }
        });
      },
    });

    /** 处理机器异常事件 */
    const hanldeRobotExceptionEvents = useCallback(
      (code: MassageRuntime.RobotExceptionEvents) => {
        if (!code) return;

        const errorOccurLevel = RobotExceptionEventsHandler[code]?.level;

        if (['l0', 'l1', 'l2'].includes(errorOccurLevel)) {
          toastify.clear();

          if (errorOccurLevel === 'l0') {
            currentIsShowL0Error.current = true;
          }
          if (errorOccurLevel === 'l1') {
            currentIsShowL1Error.current = true;
          }
          // 如果当前有l0级别的错误，则忽略l1l2级别的错误
          if (errorOccurLevel !== 'l0' && currentIsShowL0Error.current) {
            return;
          }
          // 如果当前有L1级别的错误，则忽略L2级别的错误
          if (errorOccurLevel === 'l2' && currentIsShowL1Error.current) {
            return;
          }
          Toast.clear();
          curDialogActions.current.confirm = RobotExceptionEventsHandler[code]?.confirmAction;
          curDialogActions.current.cancel = RobotExceptionEventsHandler[code]?.cancelAction;
          isIgnoreTaskFinished.current = true;
          // 如果是触发力控保护或急停事件则需要禁用弹窗的确认按钮，等待恢复事件
          if (['FORCE_CONTROL_PROTECT', 'EMERGENCY_STOP', 'HOTSTONE_DISCONNECT'].includes(code)) {
            setIsConfirmBtnDisabled(prev => ({
              ...prev,
              [code]: true,
            }));
          }

          currentErrEventsRef.current = code;
          setDiaglogParams(RobotExceptionEventsHandler[code].l1l2Params);
          show();
        } else if (errorOccurLevel === 'l3') {
          //  视觉的人体异常也属于l3级别的异常，但不需要弹窗，因此未进行配置
          const l3ParamName = RobotExceptionEventsHandler[code]?.l3Params;

          //TODO 1.6.5及以下且为人体检测相关请求不需要toastify弹窗
          if (
            isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.6.10') &&
            detectingAreaCode.includes(code)
          ) {
            return;
          }

          if (l3ParamName) {
            // 清除之前同类型的 L3 提示
            if (clearToastLoading.current[code]) {
              clearToastLoading.current[code]?.();
            }

            clearToastLoading.current[code] = toastify?.l3[RobotExceptionEventsHandler[code]?.l3Params]()?.clear;
          }
        } else {
          if (code === 'POSSIBLE_SLIPPING_WARNING') {
            if (currentServingOrder?.productItems?.[0] === 'Shockwave') {
              toastify.l4.swSlippingWarning();
            } else if (currentServingOrder?.productItems?.[0] === 'Massage') {
              toastify.l4.massageSlippingWarning();
            }
          } else {
            const l4ParamsName = RobotExceptionEventsHandler[code]?.l4Params;
            l4ParamsName && toastify.l4[l4ParamsName]();
          }
        }
      },
      [currentServingOrder?.device?.softwareVersion, currentServingOrder?.productItems, isIgnoreTaskFinished, show]
    );

    /** 处理机器异常恢复事件 */
    const hanldeRobotExceptionRecoverEvents = useCallback(
      (code: MassageRuntime.RobotExceptionEvents) => {
        const errorRecoverLevel = RobotExceptionEventsHandler[code]?.level;

        if (errorRecoverLevel === 'l0') {
          if (code === 'EMERGENCY_STOP') {
            setIsConfirmBtnDisabled(prev => ({
              ...prev,
              EMERGENCY_STOP: false,
            }));
          }
        }

        if (errorRecoverLevel === 'l2') {
          if (['FORCE_CONTROL_PROTECT', 'HOTSTONE_DISCONNECT'].includes(code)) {
            setIsConfirmBtnDisabled(prev => ({
              ...prev,
              [code]: false,
            }));
          }

          if (code === 'ARM_ERROR') {
            nav(ALL_PAGES.MEMBER_DETAIL, { replace: true });
          }
        }

        if (errorRecoverLevel === 'l3') {
          clearToastLoading.current?.[code]?.();
        }
      },
      [nav]
    );

    const showOrderErrorPrompt = useCallback(
      (config: ShowOrderErrorPromptType) => {
        const { navUrl = ALL_PAGES.MEMBER_DETAIL, navOptions, orderId, shockwaveProbeCount, finishedReason } = config;
        let params: QuitMassageFromEventParams = {
          serviceOrderId: orderId || currentServingOrder?.orderId,
          success: false,
        };
        if (
          currentServingOrder?.productItems.includes('Shockwave') &&
          isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.7.0')
        ) {
          params = {
            serviceOrderId: orderId || currentServingOrder?.orderId,
            success: false,
            shockwaveProbeCount: shockwaveProbeCount,
          };
        }

        curDialogActions.current.confirm = async () => {
          try {
            await service.massage.quitMassageFromEvent(params);
          } finally {
            nav(navUrl, navOptions);
          }
        };
        setDiaglogParams(
          finishedReason === 'ErrorWireStraightened'
            ? RobotExceptionConfig.swWireTangled
            : RobotExceptionConfig.orderException
        );
        show();
      },
      [currentServingOrder, nav, show]
    );

    useImperativeHandle(
      ref,
      () => ({
        setIsConfirmBtnDisabled,
        hanldeRobotExceptionEvents,
        hanldeRobotExceptionRecoverEvents,
        showOrderErrorPrompt,
      }),
      [hanldeRobotExceptionEvents, hanldeRobotExceptionRecoverEvents, showOrderErrorPrompt]
    );

    return (
      <div>
        <Dialog {...dialogProps} getContainer={document.body} />
      </div>
    );
  }
);

export default RobotExceptionPrompt;
